import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FileText, Coins, User, Menu, X, LogOut, Brain, Accessibility, ScrollText
} from "lucide-react";
import { HeaderLanguageSelector } from "./HeaderLanguageSelector";
import { HeaderThemeToggle } from "./HeaderThemeToggle";
import { useAuthStore } from "@/stores/auth.store";
import { useState, useEffect } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { displayLoginOrEmail } from '@/lib/student-login';

export function ModernHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();

  const formatUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName || user.lastName) {
      const formatFirstName = (name: string) => {
        return name.split(/[-\s]/).map(part =>
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        ).join('-');
      };
      const firstName = user.firstName ? formatFirstName(user.firstName) : '';
      const lastName = user.lastName ? user.lastName.toUpperCase() : '';
      return `${firstName} ${lastName}`.trim();
    }
    return user.email?.split('@')[0] || user.email || '';
  };
  const userDisplayName = formatUserDisplayName();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinks = [
    { name: t('publicHeader.nav.home'), href: "/", icon: FileText, protected: false },
    { name: t('publicHeader.nav.accessibility'), href: "/accessibility", icon: Accessibility, protected: false },
    { name: t('publicHeader.nav.leBref'), href: "/le-bref", icon: ScrollText, protected: false },
    { name: t('publicHeader.nav.useCases'), href: "/use-cases", icon: Brain, protected: false },
    { name: t('publicHeader.nav.pricing'), href: "/pricing", icon: Coins, protected: false },
    { name: t('publicHeader.nav.about'), href: "/about", icon: Brain, protected: false },
    { name: t('publicHeader.nav.contact'), href: "/contact", icon: FileText, protected: false }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full bg-[#F8FAFC] dark:bg-[#061128]" data-marketing-header>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex justify-between items-center h-20 md:h-22 rounded-2xl border border-[#E8EDF5] bg-white/95 px-4 sm:px-6 backdrop-blur-xl shadow-[0_12px_40px_rgba(12,28,56,0.08)] dark:border-[#244585] dark:bg-[#0C1C38]/95">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="flex flex-col">
              <img src="/Logo.png" alt="LYRRA Studio" className="h-14 md:h-16 w-auto dark:hidden" />
              <img src="/LogoLight.png" alt="LYRRA Studio" className="hidden h-14 md:h-16 w-auto dark:block" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50/80 p-1 dark:border-slate-700 dark:bg-slate-900/50" aria-label="Navigation principale">
            {navLinks
              .filter(link => !link.protected || user)
              .map((link) => {
                const Icon = link.icon;
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                      active
                        ? "border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,white)] bg-white text-[#0C1C38] dark:border-white/40 dark:bg-white dark:text-[#0C1C38]"
                        : "text-[#475569] hover:text-[#0F172A] hover:bg-white dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 transition-colors ${
                        active
                          ? "text-[#0C1C38]"
                          : "text-[#475569] group-hover:text-[#19335E] dark:text-slate-300 dark:group-hover:text-white"
                      }`}
                    />
                    <span>{link.name}</span>
                  </Link>
                );
              })}
            <HeaderThemeToggle />
            <HeaderLanguageSelector />
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Sélecteur de langue mobile : toujours visible en haut, pas dans le menu burger */}
            <div className="flex items-center gap-1 xl:hidden">
              <HeaderThemeToggle buttonClassName="min-h-[44px] min-w-[44px] !p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#DCE7F7] dark:bg-white/10 dark:hover:bg-white/15" />
              <HeaderLanguageSelector
                buttonClassName="min-h-[44px] min-w-[44px] !p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#DCE7F7] dark:bg-white/10 dark:hover:bg-white/15"
              />
            </div>
            {user ? (
              <div className="user-menu-container relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-2 p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#E2E8F0]/50 transition-colors"
                >
                  <User className="h-4 w-4 text-[#475569]" />
                  <span className="hidden sm:block text-sm text-[#475569]">{userDisplayName}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute end-0 mt-2 w-56 rounded-2xl bg-white border border-[#E2E8F0] py-2 shadow-xl z-[9999]">
                    <div className="px-4 py-3 border-b border-[#E2E8F0]">
                      <p className="text-sm font-medium text-[#0F172A]">{userDisplayName}</p>
                      <p className="text-xs text-[#64748B]">{displayLoginOrEmail(user.email)}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="flex items-center px-4 py-2 text-sm text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Brain className="h-4 w-4 me-3" />
                      {t('publicHeader.user.dashboard')}
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 me-3" />
                      {t('publicHeader.user.profile')}
                    </Link>
                    <div className="border-t border-[#E2E8F0] mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 me-3" />
                        {t('publicHeader.user.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors dark:text-slate-200 dark:hover:text-white">
                  {t('publicHeader.auth.login')}
                </Link>
                <Link to="/register" className="px-4 py-2 text-sm font-medium rounded-xl bg-[#1D4ED8] text-white hover:bg-[#1E3A8A] transition-all duration-200 shadow-lg shadow-[#1D4ED8]/25 hover:shadow-xl">
                  {t('publicHeader.auth.register')}
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#E2E8F0]/50 transition-colors dark:bg-white/10 dark:hover:bg-white/15"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label="Menu de navigation"
            >
              {isMenuOpen ? <X className="h-5 w-5 text-[#475569] dark:text-slate-200" /> : <Menu className="h-5 w-5 text-[#475569] dark:text-slate-200" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            id="mobile-menu"
            className="xl:hidden mt-3 max-h-[calc(100vh-7.5rem)] overflow-y-auto rounded-2xl border border-[#E2E8F0] bg-white py-4 shadow-lg dark:border-[#244585] dark:bg-[#0C1C38]"
          >
            <nav className="flex flex-col space-y-2" aria-label="Navigation mobile">
              {navLinks
                .filter(link => !link.protected || user)
                .map((link) => {
                  const Icon = link.icon;
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        active
                          ? "border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,white)] bg-white text-[#0C1C38] dark:border-white/40 dark:bg-white dark:text-[#0C1C38]"
                          : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon className={`h-5 w-5 ${active ? "text-[#0C1C38]" : "dark:text-slate-300"}`} />
                      <span className="font-medium">{link.name}</span>
                    </Link>
                  );
                })}
              {!user && (
                <div className="border-t border-[#E2E8F0] pt-4 mt-4">
                  <Link to="/login" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-white" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span className="font-medium">{t('publicHeader.auth.login')}</span>
                  </Link>
                  <Link to="/register" className="flex items-center gap-3 px-4 py-3 mt-2 rounded-xl bg-[#1D4ED8] text-white font-medium hover:bg-[#1E3A8A]" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span>{t('publicHeader.auth.register')}</span>
                  </Link>
                </div>
              )}
              {user && (
                <div className="border-t border-[#E2E8F0] pt-4 mt-4">
                  <div className="px-4 py-3 rounded-xl bg-[#DBEAFE]/30 border border-[#2563EB]/10 mb-3">
                    <p className="text-sm font-medium text-[#0F172A]">{userDisplayName}</p>
                    <p className="text-xs text-[#64748B]">{displayLoginOrEmail(user.email)}</p>
                  </div>
                  <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <Brain className="h-5 w-5" />
                    <span className="font-medium">{t('publicHeader.user.dashboard')}</span>
                  </Link>
                  <Link to="/profile" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span className="font-medium">{t('publicHeader.user.profile')}</span>
                  </Link>
                  <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors">
                    <LogOut className="h-5 w-5" />
                    <span className="font-medium">{t('publicHeader.user.logout')}</span>
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
